import { Injectable } from '@angular/core';

import { APP_CONFIG } from '../../app.config';
import { UtilHelper } from '../../shared/helpers/utils.helper';
import { RequestService } from '../../shared/services/request.service';
import { OrderStatus } from '../orders/enums/order-status.enum';
import { PaymentStatus } from '../orders/enums/payment-status.enum';
import { PickupOption } from '../orders/enums/pickup-option.enum';
import { Order } from '../orders/models/order.model';
import { ManualPayment } from './models/manual-payment.interface';
import { CustomerPayment, TablePayment, TablesPaymentList } from './models/table-payment.model';

@Injectable()
export class TablePaymentService {

	constructor(public requestService: RequestService) { }

	getTablesPayments(estabId: number): Promise<Array<TablesPaymentList>> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ establishmentId: estabId }, APP_CONFIG.WEBSERVICE.GET_TABLES_PAYMENTS);
			// const urlRequest: string = "./assets/resources/mock.json";
			this.requestService.getData(urlRequest).then((response: Array<TablePayment>) => {
				console.log(response);
				// mock
				// response = (response as any).tablesPendingPayment;

				const retorno: Array<TablesPaymentList> = [];
				if (response) {
					//Formato o response para mostrar segmentado por Setor, convertendo para tipo TablesPaymentList
					response.forEach((tablePayment: TablePayment) => {
						if (tablePayment.table) {
							const sector: string = (tablePayment.table.sector || "Geral").toUpperCase().trim();
							let sectorList: TablesPaymentList;
							const sectorIndex: number = retorno.findIndex((t: TablesPaymentList) => {
								return t.sector === sector;
							});
							//Verifica se é um setor já existente ou um novo
							if (sectorIndex >= 0) {
								sectorList = retorno[sectorIndex];
							} else {
								sectorList = new TablesPaymentList({
									sector: sector,
									list: []
								});
							}
							sectorList.list.push(new TablePayment(tablePayment));
							sectorList.list.sort((a, b) => {
								const identificationA: number | string = UtilHelper.getNumericValueIfPossible(a.table.mainIdentification);
								const identificationB: number | string = UtilHelper.getNumericValueIfPossible(b.table.mainIdentification);
								return identificationA > identificationB ? 1 : -1;
								// return a.table.mainIdentification > b.table.mainIdentification ? 1 : -1;
							});
							if (sectorIndex >= 0) {
								retorno[sectorIndex] = sectorList;
							} else {
								retorno.push(sectorList);
							}
						}
					});
				}
				resolve(retorno || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getCustomerOrdersPendingPayment(establishmentId: number, customerId: number, tableId: number): Promise<Array<Order>> {
		return new Promise((resolve, reject) => {
			const params: any = {
				establishmentId: establishmentId,
				userId: customerId,
				tableId: tableId
			};
			const urlRequest: string = this.requestService.buildUrlPathParams(params, APP_CONFIG.WEBSERVICE.GET_CUSTOMER_ORDERS_PENDING_PAYMENT);
			// const urlRequest: string = "./assets/resources/mock.json";
			this.requestService.getData(urlRequest).then((response: Array<Order>) => {
				// MOCK
				// response = (response as any).orders;
				// --------------------------------------

				const orders: Array<Order> = [];
				if (response && response.length > 0) {
					for (const o of response) {
						orders.push(new Order(o));
					}
				}
				resolve(orders || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	confirmPayment(dataPayment: ManualPayment): Promise<void> {
		return new Promise((resolve, reject) => {
			this.requestService.postData(APP_CONFIG.WEBSERVICE.CONFIRM_MANUAL_PAYMENT, dataPayment).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getCustomerOrdersPaymentPending(establishmentId: number): Promise<Array<CustomerPayment>> {
		return new Promise((resolve, reject) => {
			let urlRequest: string = this.requestService.buildUrlPathParams(
				{
					establishmentId: establishmentId,
					paymentStatus: PaymentStatus.PENDING
				},
				APP_CONFIG.WEBSERVICE.GET_ORDERS_PENDING
			);
			urlRequest += "&pickupOptions=" + PickupOption.DELIVERY + "," + PickupOption.TO_GO + "," + PickupOption.FOR_HERE + "," + PickupOption.DRIVE_IN;
			this.requestService.getData(urlRequest).then((response: any) => {
				console.log(response);

				const customers: Array<CustomerPayment> = [];
				if (response) {
					response.forEach((order: Order) => {
						const index: number = customers.findIndex((c: CustomerPayment) => {
							return c.customerId === order.userId;
						});
						if (index >= 0) {
							customers[index].totalPayment += order.total;
							customers[index].orders.push(new Order(order));
						} else {
							const customer: CustomerPayment = new CustomerPayment({
								customerId: order.userId,
								customerName: order.customerName,
								totalPayment: order.total,
								orders: [new Order(order)],
								orderStatusDescription: ""
							});
							customers.push(customer);
						}
					});
				}
				customers.sort((u1, u2) => {
					return u1.customerName > u2.customerName ? 1 : -1;
				});
				customers.forEach((c: CustomerPayment) => {
					// Ordena para poder pegar o primeiro status
					c.orders.sort((o1, o2) => {
						return o1.status['ordination'] > o2.status['ordination'] ? 1 : -1;
					});
					c.orderStatusDescription = this.getOrderStatusDescription(c.orders[0].status, c.orders.length);
				});
				console.log(customers);
				resolve(customers || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getOrderStatusDescription(status: OrderStatus, ordersCount: number): string {
		let description: string;
		switch (status) {
			case OrderStatus.RESERVED:
				description = ordersCount > 1 ? "Existem pedidos agendados" : "Pedido agendado";
				return "<span class='primary'>" + description + "</span>";

			case OrderStatus.IN_QUEUE:
				description = ordersCount > 1 ? "Existem pedidos na fila" : "Pedido na fila";
				return "<span class='primary'>" + description + "</span>";

			case OrderStatus.IN_PRODUCTION:
			case OrderStatus.PARTIAL_DELIVERED:
				description = ordersCount > 1 ? "Existem pedidos em produção" : "Pedido em produção";
				return "<span class='primary'>" + description + "</span>";

			case OrderStatus.FINISHED:
				description = ordersCount > 1 ? "Existem pedidos com entrega pendente" : "Entrega pendente";
				return "<span class='primary'>" + description + "</span>";

			default:
				description = ordersCount > 1 ? "Todos os pedidos entregues" : "Pedido entregue";
				return "<span class='green'>" + description + "</span>";
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/pages/tables-payment/tables-payment.service.ts